import { Link, router, useForm } from '@inertiajs/react'
import CreatorShell from '@/components/explore/CreatorShell'
import type { CityOption } from '@/types/publicSchool'

interface Props {
  /** Qual aba a rota renderiza: /explore/entrar → login, /explore/criar-conta → signup. */
  mode: 'login' | 'signup'
  cities: CityOption[]
}

/**
 * Login + cadastro do criador de página do BeWave Explore, numa tela só.
 *
 * A alternância entre as abas é navegação de verdade (Link entre as duas
 * rotas), não estado local: a URL diz em que modo a tela está, o servidor
 * re-renderiza o mesmo componente com o outro `mode`, e o CTA "Criar sua
 * página" pode apontar direto para o cadastro.
 *
 * Validação é do servidor, via `useForm` + `inertia: { errors }` — mesmo
 * padrão do RegisterDialog da página da escola. Nada de zod local: a fonte
 * das regras é o model Creator.
 */
export default function CreatorAuth({ mode, cities }: Props) {
  return (
    // `actions={null}`: o default do header traz o menu com as duas contas da
    // BeWave — nesta tela, que JÁ é o login do criador, oferecer de novo as
    // portas de entrada só confundiria.
    <CreatorShell
      title="Criar sua página — BeWave Explore"
      cities={cities}
      actions={null}
      backdrop
    >
      <div className="flex flex-1 items-start justify-center px-5 py-12 sm:px-6 sm:py-16">
        {/* Título, abas e formulário num cartão SÓ — igual ao /sign_in do admin.
            Soltos sobre o fundo eles caíam em cima do letreiro "BEWAVE" da
            ilustração e ficavam ilegíveis. */}
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <h1 className="text-center text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
            {mode === 'signup' ? 'Crie sua página' : 'Bem-vindo de volta'}
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            {mode === 'signup'
              ? 'Comece criando sua conta de criador — escola, pousada, fotógrafo, o que for do mar.'
              : 'Entre na sua conta de criador do BeWave Explore.'}
          </p>

          <ModeTabs mode={mode} />

          <div className="mt-6">
            {mode === 'signup' ? <SignupForm /> : <LoginForm />}
          </div>
        </div>
      </div>
    </CreatorShell>
  )
}

function ModeTabs({ mode }: { mode: Props['mode'] }) {
  const tab = (active: boolean) =>
    `flex-1 rounded-full py-2 text-center text-sm font-semibold transition-colors ${
      active
        ? 'bg-white text-gray-900 shadow-sm'
        : 'text-gray-500 hover:text-gray-700'
    }`

  return (
    <div className="mt-6 flex rounded-full bg-gray-100 p-1">
      <Link href="/entrar" className={tab(mode === 'login')}>
        Entrar
      </Link>
      <Link href="/criar-conta" className={tab(mode === 'signup')}>
        Criar conta
      </Link>
    </div>
  )
}

function LoginForm() {
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    password: '',
  })

  // `verification` não é campo do form: é a chave que o servidor usa quando a
  // conta existe mas o e-mail ainda não foi confirmado — mostra o aviso com o
  // botão de reenvio em vez de um erro de campo.
  const verificationError = (errors as Record<string, string | string[]>)
    .verification

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/entrar')
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {verificationError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p>
            {Array.isArray(verificationError)
              ? verificationError[0]
              : verificationError}
          </p>
          <button
            type="button"
            onClick={() =>
              router.post('/confirme-email', { email: data.email })
            }
            className="mt-2 font-semibold underline underline-offset-2 hover:opacity-80"
          >
            Reenviar e-mail de confirmação
          </button>
        </div>
      )}

      <Field label="Email" error={errors.email}>
        <TextInput
          type="email"
          value={data.email}
          onChange={(v) => setData('email', v)}
          placeholder="voce@email.com"
        />
      </Field>
      <Field label="Senha" error={errors.password}>
        <TextInput
          type="password"
          value={data.password}
          onChange={(v) => setData('password', v)}
          placeholder="Sua senha"
        />
      </Field>

      <SubmitButton processing={processing}>
        {processing ? 'Entrando...' : 'Entrar'}
      </SubmitButton>
    </form>
  )
}

function SignupForm() {
  const { data, setData, post, processing, errors } = useForm({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/criar-conta')
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Nome" error={errors.first_name}>
          <TextInput
            value={data.first_name}
            onChange={(v) => setData('first_name', v)}
            placeholder="Seu nome"
          />
        </Field>
        <Field label="Sobrenome" error={errors.last_name}>
          <TextInput
            value={data.last_name}
            onChange={(v) => setData('last_name', v)}
            placeholder="Seu sobrenome"
          />
        </Field>
      </div>
      <Field label="Email" error={errors.email}>
        <TextInput
          type="email"
          value={data.email}
          onChange={(v) => setData('email', v)}
          placeholder="voce@email.com"
        />
      </Field>
      <Field label="Telefone" error={errors.phone}>
        <TextInput
          value={data.phone}
          onChange={(v) => setData('phone', formatPhone(v))}
          placeholder="(85) 99999-9999"
          inputMode="numeric"
        />
      </Field>
      <Field label="Senha" error={errors.password}>
        <TextInput
          type="password"
          value={data.password}
          onChange={(v) => setData('password', v)}
          placeholder="Mínimo 8 caracteres"
        />
      </Field>
      <Field label="Confirmar senha" error={errors.password_confirmation}>
        <TextInput
          type="password"
          value={data.password_confirmation}
          onChange={(v) => setData('password_confirmation', v)}
          placeholder="Repita a senha"
        />
      </Field>

      <SubmitButton processing={processing}>
        {processing ? 'Criando conta...' : 'Criar minha conta'}
      </SubmitButton>
    </form>
  )
}

function SubmitButton({
  processing,
  children,
}: {
  processing: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="submit"
      disabled={processing}
      className="mt-2 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      {children}
    </button>
  )
}

/* Mesmos helpers locais do RegisterDialog da página da escola — pequenos de
   propósito; se um terceiro formulário público aparecer, aí viram módulo. */

const onlyDigits = (value: string) => value.replace(/\D/g, '')

// Máscara de telefone BR: (85) 99999-9999 / (85) 9999-9999
const formatPhone = (value: string) => {
  const d = onlyDigits(value).slice(0, 11)
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  inputMode,
}: {
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
    />
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string | string[]
  children: React.ReactNode
}) {
  const message = Array.isArray(error) ? error[0] : error
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {children}
      {message && <p className="text-xs text-red-600">{message}</p>}
    </div>
  )
}
